/* eslint-disable no-prototype-builtins */
/* eslint-disable import/no-cycle */
/* eslint-disable no-use-before-define */
import app from 'new-ui/app';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { batch } from 'react-redux';
import { authenticationActions } from 'components/Authentication/actions';
import LANGUAGE from 'components/AppTranslationWrapper/constants/language';
import REDUX_ACTIONS from 'components/Questionnaire/constants/reduxActions';
import { QUESTION_ID } from 'modules/questionnaire/questionIds';
import enumsThatSupportOptionsFromServer from 'components/Questionnaire/constants/enumsThatSupportOptionsFromServer';
import SECTIONS from 'components/Questionnaire/constants/questionSections';
import { ANALYTICS_ACTION_TYPES } from 'functions/analytics/constants/actionTypes';
import analytics from 'functions/analytics';
import { geoActions } from 'utils/actions/geo';
import { requestUserCountry } from 'modules/user/requestUserCountry';
import { getQuestionnaireQuestions, conditionsChecker, isIndicationFromBackend } from 'components/Questionnaire/functions';
import getFormValue from 'components/Questionnaire/functions/getFormValue';
import { questionnaireApi } from 'modules/questionnaire/api';
import formatMonthPickerValue from 'components/Questionnaire/functions/formatMonthPickerValue';
import { formatOptionsFromServerData } from 'components/Questionnaire/functions/formatOptionsFromServerData';
import { LatestMatchesFlow } from 'modules/matches/latesetMatchesFlow';
import { intercom } from 'modules/intercom/intercom';
import { DISEASES, GENERAL_DISEASE } from 'utils/constants/disease';
import { GA_QUESTIONNAIRE_CATEGORY, GA_QUESTIONNAIRE_EVENTS } from 'modules/ga/generalEvents';
import {
  DISEASE_SUB_TYPE_QUESTION_ID_BY_DISEASE,
  TREATMENT_QUESTION_ID_BY_DISEASE,
} from 'modules/questionnaire/constants';
import { getDiseaseOptions } from 'modules/questionnaire/helpers';
import { getUserCondition } from 'modules/user/utils';
import { mapFormToConditionProfile } from './functions/mapFormToConditionProfile';
import { getQuestionnaireForm } from './functions/mapConditionProfileToForm';
import { findIndexOfQuestionInQstack, getQuestionIdByIndex } from './helpers';
import { selectOptionsFromServer } from './selectors';
import {
  setUploadedQuestionnaireFiles,
  addQuestionnaireFiles,
  removeQuestionnaireFiles,
  uploadQuestionnaireFiles,
  resetUploadedQuestionnaireFiles,
} from './filesActions';
import { NEW_CANCER_TYPES } from './constants/newCancerTypes';
import { getAdditionalParams } from './functions/getAdditionalParams';

export {
  setUploadedQuestionnaireFiles,
  addQuestionnaireFiles,
  removeQuestionnaireFiles,
  uploadQuestionnaireFiles,
  resetUploadedQuestionnaireFiles,
};

const { [GENERAL_DISEASE]: GENERAL_DISEASE_SUB_TYPE_QUESTION_ID } = DISEASE_SUB_TYPE_QUESTION_ID_BY_DISEASE;

export const setQuestionnaireInitialized = (payload) => ({
  type: REDUX_ACTIONS.QUESTIONNAIRE_STATE_SET,
  payload: { isInitialized: payload },
});

export const setQuestionnaireState = (payload) => ({
  type: REDUX_ACTIONS.QUESTIONNAIRE_STATE_SET,
  payload,
});

export const setQuestionnaireStep = (payload) => ({
  type: REDUX_ACTIONS.QUESTIONNAIRE_STEP,
  payload,
});

export const setIsFetching = (payload) => ({
  type: REDUX_ACTIONS.IS_FETCHING_SET,
  payload,
});

export const setSidebarVisibility = (payload) => ({
  type: REDUX_ACTIONS.SET_SIDEBAR_VISIBILITY,
  payload,
});

export const setFieldValue = (payload) => ({
  type: REDUX_ACTIONS.FORM_FIELD_VALUE_SET,
  payload: {
    id: payload.id,
    value: payload.value,
  },
});

const patchForm = (payload) => ({
  type: REDUX_ACTIONS.PATCH_FORM_FIELDS,
  payload,
});

export const setSkipStep = () => ({
  type: REDUX_ACTIONS.QUESTIONNAIRE_SKIP_STEP,
});

export const setIsLastQuestion = {
  type: REDUX_ACTIONS.IS_LAST_QUESTION,
};

export const setIsNotLastQuestion = {
  type: REDUX_ACTIONS.IS_NOT_LAST_QUESTION,
};

const fetchQuestionOptions = (onComplete, condition, additionalParams) => async (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.GET_QUESTION_OPTIONS_REQUEST,
  });
  const payload = {};
  try {
    await Promise.all(Object.values(enumsThatSupportOptionsFromServer).map(async (questionId) => {
      const result = questionId === enumsThatSupportOptionsFromServer.drugs_received && !NEW_CANCER_TYPES.includes(condition)
        ? await questionnaireApi.getQuestionOptions(questionId, condition, additionalParams)
        : await questionnaireApi.getQuestionOptions(questionId, condition);
      if (!result.data) {
        throw new Error('no data in response');
      }
      payload[questionId] = await formatOptionsFromServerData(result.data, questionId);
      payload[questionId].responseData = result.data;
    }));
  } catch (ex) {
    console.error('Error occurred, while getting question options from server:', ex);

    batch(() => {
      dispatch({
        type: REDUX_ACTIONS.GET_QUESTION_OPTIONS_FAILURE,
        payload: ex,
      });
      dispatch(setIsFetching(false));
    });
    return;
  }

  dispatch({
    type: REDUX_ACTIONS.GET_QUESTION_OPTIONS_SUCCESS,
    payload,
  });

  if (typeof onComplete === 'function') {
    onComplete();
  }
  dispatch(setIsFetching(false));
};

export const goToFastSignUp = (shouldShowQuickSignupPopup = false) => (dispatch, getState) => {
  const state = getState();
  const {
    profile: { data: profile },
    questionnaire: { currentIndex: currentQuestionIndex, qstack },
  } = state;

  const questionId = getQuestionIdByIndex(currentQuestionIndex, qstack);

  analytics.quickSignUpStarted({ questionId });
  dispatch(setIsFetching(true));

  const gaEvent = DISEASES.includes(getUserCondition(profile))
    ? `${GA_QUESTIONNAIRE_EVENTS.QUICK_SIGNUP_POPUP_VIEW_INTAKE}_${profile?.personal?.condition}`
    : `${GA_QUESTIONNAIRE_EVENTS.QUICK_SIGNUP_POPUP_VIEW}_${profile?.personal?.condition}`;
  app.sendGoogleAnalyticsEvent(GA_QUESTIONNAIRE_CATEGORY, gaEvent);

  dispatch(authenticationActions.startUserRegistration({
    profileData: profile,
    quickSignup: true,
    isModalSignup: shouldShowQuickSignupPopup,
  }));
};

// reformat optionsFromServer in the case ui language is changed
export const reformatOptionsFromServer = async (dispatch, getState) => {
  const optionsFromServer = selectOptionsFromServer(getState());

  if (!optionsFromServer) {
    return;
  }

  try {
    const payload = { ...optionsFromServer };
    await Promise.all(Object.values(enumsThatSupportOptionsFromServer).map(async (questionId) => {
      if (!optionsFromServer[questionId]) {
        return;
      }
      // take preserved response data
      const responseData = optionsFromServer[questionId]?.responseData;
      if (!responseData) {
        // invariant
        payload[questionId] = optionsFromServer[questionId];
        return;
      }
      payload[questionId] = await formatOptionsFromServerData(responseData, questionId);
      payload[questionId].responseData = responseData;
    }));
    dispatch({
      type: REDUX_ACTIONS.GET_QUESTION_OPTIONS_SUCCESS,
      payload,
    });
  } catch (error) {
    console.error('reformatOptionsFromServer action error', error);
  }
};

export const fetchDiseases = () => async (dispatch) => {
  const data = await getDiseaseOptions();
  dispatch({ type: REDUX_ACTIONS.GET_ACTIVE_DISEASE, payload: [...data] });
};

export const initQuestionnaire = ({ condition, questionId, targetQuestionNotFoundAction }) => async (dispatch, getState) => {
  const state = getState();
  const {
    profile: { data: profile }, questionnaire, authentication, main,
  } = state;
  const { optionsFromServer, isFetchingOptionsFromServer } = questionnaire;
  const additionalParams = getAdditionalParams(profile);

  if (
    // eslint-disable-next-line
    Object.values(enumsThatSupportOptionsFromServer).length !== Object.values(optionsFromServer).filter((i) => !!i).length
    && !isFetchingOptionsFromServer
  ) {
    // This will fetch the biomarker and drug received options
    const onGottenOptionsFromServer = () => {
      dispatch(initQuestionnaire({ condition, questionId, targetQuestionNotFoundAction }));
    };

    dispatch(fetchQuestionOptions(
      onGottenOptionsFromServer,
      profile?.personal?.condition,
      additionalParams,
    ));

    return;
  }

  const allQuestions = await getQuestionnaireQuestions(condition, additionalParams);

  analytics.userIsMovedToTheNextSection({
    currentSection: null,
    nextSection: (allQuestions || [])[0].section || SECTIONS.ABOUT_ME,
    actionType: ANALYTICS_ACTION_TYPES.INDICATION_CHOSEN,
  });

  dispatch(setQuestionnaireState({ allQuestions }));

  const { personal: { conditionSubType } = {} } = profile;
  const form = await getQuestionnaireForm(profile, questionnaire);

  if (condition === GENERAL_DISEASE && conditionSubType) {
    form[GENERAL_DISEASE_SUB_TYPE_QUESTION_ID] = {
      value: conditionSubType,
      label: `questionnaire.questions.${GENERAL_DISEASE}.${GENERAL_DISEASE_SUB_TYPE_QUESTION_ID}.option.${conditionSubType}`,
    };
    dispatch(
      setFieldValue({
        id: GENERAL_DISEASE_SUB_TYPE_QUESTION_ID,
        value: {
          value: conditionSubType,
          label: `questionnaire.questions.${GENERAL_DISEASE}.${GENERAL_DISEASE_SUB_TYPE_QUESTION_ID}.option.${conditionSubType}`,
        },
      }),
    );
  }

  const loggedIn = !!(authentication && authentication.loggedIn && authentication.user);

  if (!loggedIn) {
    const _condition = getUserCondition(profile);
    const _event = DISEASES.includes(_condition)
      ? `${GA_QUESTIONNAIRE_EVENTS.QUESTIONNAIRE_STARTED_INTAKE}_${_condition}`
      : `${GA_QUESTIONNAIRE_EVENTS.QUESTIONNAIRE_STARTED}_${_condition}`;

    app.sendGoogleAnalyticsEvent(GA_QUESTIONNAIRE_CATEGORY, _event);
  }

  // Must update `qstack` before continuing
  const qstack = updateQStack({
    allQuestions,
    form,
    dispatch,
    profile,
    state,
  });

  if (main.lang === LANGUAGE.he.SHORT) {
    const isrealLocation = {
      location: {
        lat: 31.76318669999999,
        lng: 35.2056087,
      },
      label: 'Israel Aharoni Street, Jerusalem, Israel',
      name: 'Israel',
    };
    dispatch(setGeoLocation(isrealLocation));

    form.travel_limit = '200';
  } else {
    const { info: { country } } = profile;
    dispatch(setGeoLocation(country || {}));
  }

  let currentIndex;
  if (!questionId) {
    currentIndex = 0;
  } else {
    currentIndex = findIndexOfQuestionInQstack(questionId, qstack);

    if (currentIndex < 0) {
      currentIndex = 0;

      if (targetQuestionNotFoundAction) {
        dispatch(targetQuestionNotFoundAction(questionId));
      }
    }
  }
  batch(() => {
    dispatch(setQuestionnaireState({ currentIndex, submitted: false, form }));
    dispatch(setUploadedQuestionnaireFiles(profile?.personal?.documents_data ?? []));
    dispatch(setIsFetching(false));
    dispatch(setQuestionnaireInitialized(true));
  });
};

export const goToQuestion = ({ currentIndex }) => setQuestionnaireState({ currentIndex, submitted: false });

export const infoAttributes = [
  'given_name',
  'first_name',
  'family_name',
  'last_name',
  'phone',
  'last_location',
  'user_role',
  'registered_at',
  'last_modified_at',
  'email',
  'preferred_location',
  'country',
  'partner',
  'gender',
  'dob',
  'user_type',
  'looking_for',
];

const updateUser = async (isDone = null, dispatch, state) => {
  const {
    authentication, questionnaire, profile: { data: profile },
  } = state;
  const loggedIn = !!(authentication && authentication.loggedIn && authentication.user);
  const {
    form, qstack, allQuestions, currentIndex: currentQuestionIndex,
  } = questionnaire;

  const geoLocation = state.geo_location;

  const user = (loggedIn && authentication.user) || {};
  const toUpdate = {
    info: {
      user_wix_id: user.user_wix_id || null,
      // country: null,
      email: user.email || null,
      family_name: form.family_name || null,
      phone: user.phone || null,
      user_type:
        getFormValue(form.user_type) || user.user_type || profile.info.user_type || profile.condition_profile.user_type,
    },
    personal: {
      user_wix_id: user.user_wix_id || null,
      profile_id: user.user_wix_id ? user.profile_id || profile.personal.profile_id || null : null,
      patient_id: user.user_wix_id ? user.patient_id || profile.personal.patient_id || null : null,
      condition: profile.personal.condition,
      gender: getFormValue(form.gender),
      dob: formatMonthPickerValue(form.dob)?.format('YYYY-MM-DD') || null,
      travel_limit: getFormValue(form.travel_limit),
      looking_for: getFormValue(form.looking_for),
    },
    condition_profile: {},
  };

  const currentQuestionId = getQuestionIdByIndex(currentQuestionIndex, qstack);
  const isInfoAttribute = infoAttributes.includes(currentQuestionId);

  let conditionProfile;
  if (isIndicationFromBackend(profile.personal.condition)) {
    conditionProfile = {
      [QUESTION_ID.tnm]: [],
      [QUESTION_ID.treatments.questionId]: [],
      [QUESTION_ID.procedures.questionId]: [],
      ...mapFormToConditionProfile(form, allQuestions),
      qstack: profile && isInfoAttribute ? profile.condition_profile.qstack : qstack.map((q) => q.id),
    };

    for (const attribute in conditionProfile) {
      if (attribute === 'preferred_location') {
        delete conditionProfile[attribute];
      }
    }
  } else {
    conditionProfile = {
      [QUESTION_ID.treatments.questionId]: [],
      [QUESTION_ID.procedures.questionId]: [],
      ...mapFormToConditionProfile(form, allQuestions),
      qstack: profile && isInfoAttribute ? profile.condition_profile.qstack : qstack.map((q) => q.id),
    };

    // Removes personal / info attributes from the condition_profile
    for (const attribute in conditionProfile) {
      if (attribute in toUpdate.personal || infoAttributes.includes(attribute)) {
        if (![QUESTION_ID.dob, QUESTION_ID.gender].includes(attribute)) {
          delete conditionProfile[attribute];
        }
      }
    }
  }

  if (!isEmpty(geoLocation)) {
    toUpdate.info = {
      ...toUpdate.info,
      country: geoLocation,
    };
  }

  toUpdate.condition_profile = conditionProfile;

  if (
    !isEqual(profile.condition_profile, toUpdate.condition_profile)
    || !isEqual(profile.personal, toUpdate.personal)
  ) {
    await dispatch(authenticationActions.updateUser({ user: toUpdate, loggedIn }));
  }

  if (isDone) {
    intercom.updateUserAttributes({ quick_signup: false, email: user.email }, profile.personal.user_wix_id);
  }
  return toUpdate;
};

export const skipStep = ({ beforeNextStepAction, changeStepAction }) => (dispatch, getState) => {
  const {
    form, currentIndex, qstack,
  } = getState().questionnaire;
  if (!qstack[currentIndex]) {
    return;
  }

  const { id } = qstack[currentIndex];

  if (form[id] === undefined) {
    dispatch(
      setFieldValue({
        id,
        value: null,
      }),
    );
  }

  dispatch(nextStep({ isSkip: true, beforeNextStepAction, changeStepAction }));
};

export const backStep = ({ changeStepAction }) => (dispatch, getState) => {
  dispatch(setQuestionnaireStep('back'));
  const { profile: { data: profile }, questionnaire } = getState();

  const { currentIndex, qstack } = questionnaire;
  if (currentIndex <= 0) {
    return;
  }

  let backStepIndex = currentIndex - 1;

  const { personal: { condition } } = profile;

  if (condition === GENERAL_DISEASE && getQuestionIdByIndex(backStepIndex, qstack) === GENERAL_DISEASE_SUB_TYPE_QUESTION_ID) {
    // skip GENERAL_DISEASE_SUB_TYPE_QUESTION_ID
    backStepIndex -= 1;
  }

  if (backStepIndex < 0) {
    return;
  }

  if (currentIndex !== backStepIndex) {
    analytics.userIsMovedToTheNextSection({
      currentSection: qstack[currentIndex].section,
      nextSection: qstack[backStepIndex].section,
      actionType: ANALYTICS_ACTION_TYPES.STEP_BACK,
    });
  }

  batch(() => {
    dispatch(
      setQuestionnaireState({
        currentIndex: backStepIndex,
      }),
    );
    // is supposed to be the last
    if (changeStepAction) {
      dispatch(changeStepAction());
    }
  });
};

export const updateQStack = ({
  allQuestions, form = {}, dispatch = () => { }, state,
}) => {
  const conditionProfile = mapFormToConditionProfile(form, allQuestions);

  const qstack = [];
  const qstackHidden = [];

  allQuestions.forEach((_question, index) => {
    const question = { ..._question };
    const shouldQuestionBeShown = conditionsChecker(conditionProfile, _question.conditions, state);
    const nextQuestionIndex = state ? state.questionnaire.currentIndex + 1 : 1;
    if (!shouldQuestionBeShown) {
      qstackHidden.push(question.id);
      return;
    }
    if (nextQuestionIndex === index && Array.isArray(question?.options)) {
      question.options = question?.options?.filter((option) => conditionsChecker({}, option.conditions, state));
    }
    qstack.push(question);
  });
  batch(() => {
    qstackHidden.forEach((question) => {
      dispatch(
        setFieldValue({
          id: question,
          value: null,
        }),
      );
    });
  });

  dispatch(setQuestionnaireState({ qstack }));

  return qstack;
};

const setGeoLocation = (value) => (dispatch) => {
  dispatch(geoActions.setGeoLocation(value));

  dispatch(
    setFieldValue({
      id: QUESTION_ID.preferred_location,
      value,
    }),
  );
};

export const nextStep = ({ isSkip, beforeNextStepAction, changeStepAction } = {}) => async (dispatch, getState) => {
  let state = getState();
  const {
    questionnaire, profile: { data: profile },
  } = state;
  if (questionnaire.isSidebarOpen) {
    dispatch(setSidebarVisibility(false));
    return;
  }

  const { currentIndex: prevQIndex, qstack: prevQstack } = questionnaire;

  const qstack = updateQStack({
    allQuestions: questionnaire.allQuestions,
    form: questionnaire.form,
    dispatch,
    profile,
    state,
  });

  state = getState();

  if (!qstack[prevQIndex]) {
    return;
  }

  const prevQuestionId = getQuestionIdByIndex(prevQIndex, prevQstack);

  if (!isSkip) {
    analytics.clickNextOnAQuestion({
      questionId: prevQuestionId,
    });
  }

  const treatmentForIndicationQuestionId = TREATMENT_QUESTION_ID_BY_DISEASE[profile.personal.condition];
  const treatmentForIndicationQuestion = profile.condition_profile[treatmentForIndicationQuestionId];
  // !!! mutation
  if (!profile.personal.answered_no_on_has_treatment_date && treatmentForIndicationQuestion === 'no') {
    profile.personal.answered_no_on_has_treatment_date = Date.now();
  } else if (treatmentForIndicationQuestion !== 'no') {
    profile.personal.answered_no_on_has_treatment_date = null;
  }

  let nextQIndex = prevQIndex + 1;
  const { personal: { condition } } = profile;

  if (condition === GENERAL_DISEASE && getQuestionIdByIndex(nextQIndex, qstack) === GENERAL_DISEASE_SUB_TYPE_QUESTION_ID) {
    // skip GENERAL_DISEASE_SUB_TYPE_QUESTION_ID
    nextQIndex += 1;
  }

  const isNotLastStep = nextQIndex < qstack.length;

  let updatedData = null;
  if (qstack[prevQIndex].id === QUESTION_ID.preferred_location) {
    const { geo_location: geoLocation } = state;
    if (geoLocation?.label) {
      try {
        updatedData = await updateUser(false, dispatch, state);
      } catch (error) {
        console.error(error);
      }
    } else if (profile?.info && !profile.info.country?.location) {
      // checks if preferredLocation was answered
      let userLocation;
      try {
        const locationData = await requestUserCountry();
        dispatch(setGeoLocation(locationData));
      } catch (e) {
        // console.error("Can't get user browser location", e);
      }

      try {
        updatedData = await updateUser(!userLocation, dispatch, state);
      } catch (e) {
        console.error(e);
      }
    } else {
      try {
        updatedData = await updateUser(true, dispatch, state);
      } catch (error) {
        console.error(error);
      }
    }
  } else {
    try {
      updatedData = await updateUser(!isNotLastStep, dispatch, state);
    } catch (error) {
      console.error(error);
    }
  }

  if (isNotLastStep) {
    LatestMatchesFlow.onUpdateMedicalCondition();

    if (beforeNextStepAction) {
      const shouldContinue = await dispatch(beforeNextStepAction({
        qstack,
        currentQIndex: prevQIndex,
        nextQIndex,
        isSkip,
        currentQuestionId: prevQuestionId,
        nextQuestionId: getQuestionIdByIndex(nextQIndex, qstack),
        isLastQuestion: false,
        profile: updatedData,
      }));
      if (shouldContinue === false) {
        return;
      }
    }

    dispatch(
      setQuestionnaireState({
        currentIndex: nextQIndex,
      }),
    );
  } else if (!questionnaire.submitted) {
    LatestMatchesFlow.onQuestionnairePass();

    dispatch(
      setQuestionnaireState({
        submitted: true,
      }),
    );

    if (beforeNextStepAction) {
      const shouldContinue = await dispatch(beforeNextStepAction({
        qstack,
        currentQIndex: prevQIndex,
        currentQuestionId: prevQuestionId,
        isSkip,
        isLastQuestion: true,
        profile: updatedData,
      }));
      if (shouldContinue === false) {
        return;
      }
    }
  }

  batch(() => {
    dispatch(setQuestionnaireStep('next'));

    if (qstack.length === nextQIndex) {
      dispatch(setIsLastQuestion);
    }

    // is supposed to be the last
    if (changeStepAction) {
      dispatch(changeStepAction());
    }
  });
};

export const setFormFromProfile = (profile) => async (dispatch, getState) => {
  const { questionnaire } = getState();
  const form = await getQuestionnaireForm(profile, questionnaire);
  dispatch(patchForm(form));
};
